import { useState } from 'react';
import { Chip, Stack, Button, Alert, Snackbar, IconButton, Tooltip } from '@mui/material';
import { ListAlt } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { epcRequisitionApi, type EpcRequisition } from '../../api/epcProcurement.api';
import { RequisitionDetailDialog, stageColor } from './EpcRequisitionsPage';

const procColor = (s?: string): 'warning' | 'success' | 'error' | 'default' =>
  s === 'accepted' ? 'success' : s === 'rejected' ? 'error' : s === 'pending' ? 'warning' : 'default';
const FILTERS: Array<{ key: string; label: string }> = [
  { key: 'pending', label: 'Pending' }, { key: 'accepted', label: 'Accepted' }, { key: 'rejected', label: 'Rejected' }, { key: '', label: 'All' },
];

/** Procurement team's queue: approved EPC requisitions to accept / reject / edit before ordering. */
export function EpcProcurementReviewPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [proc, setProc] = useState('pending');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Pending = approved & awaiting procurement. Accepted/Rejected stay visible even after the
  // requisition is ordered (status becomes 'ordered'), so filter by procurement_status alone there.
  const filterParams = proc === 'pending' ? { status: 'approved', procurementStatus: 'pending' }
    : proc === 'accepted' ? { procurementStatus: 'accepted' }
    : proc === 'rejected' ? { procurementStatus: 'rejected' }
    : {};
  const { data, isLoading } = useQuery({
    queryKey: ['epc-proc-review', page, limit, proc],
    queryFn: () => epcRequisitionApi.list({ page, limit, ...filterParams }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['epc-proc-review'] });

  const columns: Column<EpcRequisition>[] = [
    { key: 'requisitionCode', label: 'Requisition' },
    { key: 'clientName', label: 'Client', render: (r) => r.clientName ?? '—' },
    { key: 'projectName', label: 'Project', render: (r) => r.projectName ? `${r.projectName}${r.projectCode ? ` (${r.projectCode})` : ''}` : `#${r.projectId}` },
    { key: 'siteName', label: 'Site', render: (r) => r.siteName ? `${r.siteName}${r.siteCode ? ` (${r.siteCode})` : ''}` : '—' },
    { key: 'stateName', label: 'State', render: (r) => r.stateName ?? '—' },
    { key: 'raisedByName', label: 'Raised By', render: (r) => r.raisedByName ?? '—' },
    { key: 'createdAt', label: 'Request Date', render: (r) => new Date(r.createdAt).toLocaleDateString('en-IN') },
    { key: 'procurementStage', label: 'Stage', render: (r) => <Chip size="small" color={stageColor(r.procurementStage)} label={r.procurementStage ?? '—'} /> },
    { key: 'procurementStatus', label: 'Procurement', render: (r) => <Chip size="small" color={procColor(r.procurementStatus)} label={r.procurementStatus ?? 'pending'} /> },
  ];

  return (
    <>
      <PageHeader title="Procurement Review" subtitle="Approved requisition requests awaiting the procurement team — review, edit, accept or reject before ordering." />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {FILTERS.map((f) => (
          <Button key={f.key || 'all'} size="small" variant={proc === f.key ? 'contained' : 'outlined'} onClick={() => { setProc(f.key); setPage(1); }}>{f.label}</Button>
        ))}
      </Stack>

      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => <Tooltip title="Review"><IconButton size="small" onClick={() => setDetailId(r.id)}><ListAlt fontSize="small" /></IconButton></Tooltip>} />

      {detailId && <RequisitionDetailDialog id={detailId} procurementMode onClose={() => setDetailId(null)} onChanged={invalidate} onToast={setToast} />}

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
